import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './ui/Icon';
import type { AssistantSuggestedQueryPill } from '../constants/assistantSuggestedQueries';
import type { AssistantPageKey } from '../constants/assistantSuggestedQueries';
import type { AssistantPageContext } from '../hooks/useDashboardAssistantContext';

const TIP_DISMISSED_KEY = 'ezacc-gva-empty-tip-dismissed';

const PAGE_TIP_KEYS: Partial<Record<AssistantPageKey, string>> = {
  dashboard: 'voice.emptyState.tips.dashboard',
  quotes: 'voice.emptyState.tips.quotes',
  invoicesReceivable: 'voice.emptyState.tips.invoicesReceivable',
  invoicesPayable: 'voice.emptyState.tips.invoicesPayable',
  contacts: 'voice.emptyState.tips.contacts',
  expenses: 'voice.emptyState.tips.expenses',
  reports: 'voice.emptyState.tips.reports',
  journalEntries: 'voice.emptyState.tips.journalEntries',
  bankAccounts: 'voice.emptyState.tips.bankAccounts',
  inventory: 'voice.emptyState.tips.inventory',
};

function readTipDismissed(): boolean {
  try {
    return localStorage.getItem(TIP_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function formatMetric(value: number | undefined, currency = 'AED'): string {
  if (value == null || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return String(Math.round(value));
  }
}

export interface VoiceAssistantRailEmptyStateProps {
  pageSuggestedQueries: AssistantSuggestedQueryPill[];
  assistantContext?: AssistantPageContext;
  disabled?: boolean;
  onSuggestedQuery: (command: string) => void;
}

const VoiceAssistantRailEmptyState: React.FC<VoiceAssistantRailEmptyStateProps> = ({
  pageSuggestedQueries,
  assistantContext,
  disabled = false,
  onSuggestedQuery,
}) => {
  const { t } = useTranslation();
  const [tipDismissed, setTipDismissed] = useState(readTipDismissed);

  const dismissTip = useCallback(() => {
    setTipDismissed(true);
    try {
      localStorage.setItem(TIP_DISMISSED_KEY, '1');
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const restoreTip = useCallback(() => {
    setTipDismissed(false);
    try {
      localStorage.removeItem(TIP_DISMISSED_KEY);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const pageKey = assistantContext?.pageKey ?? 'default';
  const pageName = assistantContext?.pageName
    || t(`navigation.${pageKey === 'default' ? 'dashboard' : pageKey}`, 'This page');
  const pageTipKey = PAGE_TIP_KEYS[pageKey] || 'voice.emptyState.tips.default';
  const pageTip = t(pageTipKey, 'Speak or type a command — try one of the suggestions below.');
  const quickActions = pageSuggestedQueries.slice(0, 4);

  const showDashboardMetrics = pageKey === 'dashboard' && (
    assistantContext?.cashPosition != null
    || assistantContext?.receivables != null
    || assistantContext?.pendingReviews != null
  );
  const currency = assistantContext?.currency || 'AED';

  return (
    <div className="gva-rail-empty-state" data-testid="gva-rail-empty-state">
      {!tipDismissed && (
        <div className="gva-rail-empty-tip">
          <p className="gva-rail-empty-tip-text">
            {t(
              'voice.emptyHint',
              'Type a message below, tap Start Speaking, or choose a suggested query.'
            )}
          </p>
          <button
            type="button"
            className="gva-rail-empty-tip-dismiss"
            onClick={dismissTip}
            aria-label={t('voice.dismissTip', 'Dismiss getting started tip')}
            title={t('voice.dismissTip', 'Dismiss getting started tip')}
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      )}

      <div className="gva-rail-empty-hero">
        <div className="gva-rail-empty-hero-icon" aria-hidden="true">
          <Icon name="app-logo" size={22} />
        </div>
        <div className="gva-rail-empty-hero-copy">
          <p className="gva-rail-empty-page">{pageName}</p>
          <p className="gva-rail-empty-subtitle">{pageTip}</p>
        </div>
      </div>

      {showDashboardMetrics && (
        <div className="gva-rail-empty-metrics" aria-label={t('voice.emptyState.metricsLabel', 'Key metrics')}>
          {assistantContext?.cashPosition != null && (
            <div className="gva-rail-empty-metric">
              <span className="gva-rail-empty-metric-label">
                {t('dashboard.cashOnHand', 'Cash on Hand')}
              </span>
              <span className="gva-rail-empty-metric-value">
                {formatMetric(assistantContext.cashPosition, currency)}
              </span>
            </div>
          )}
          {assistantContext?.receivables != null && (
            <div className="gva-rail-empty-metric">
              <span className="gva-rail-empty-metric-label">
                {t('dashboard.totalReceivables', 'Total Receivables')}
              </span>
              <span className="gva-rail-empty-metric-value">
                {formatMetric(assistantContext.receivables, currency)}
              </span>
            </div>
          )}
          {assistantContext?.pendingReviews != null && assistantContext.pendingReviews > 0 && (
            <div className="gva-rail-empty-metric gva-rail-empty-metric--alert">
              <span className="gva-rail-empty-metric-label">
                {t('dashboard.pendingReview', 'Pending review')}
              </span>
              <span className="gva-rail-empty-metric-value">{assistantContext.pendingReviews}</span>
            </div>
          )}
        </div>
      )}

      {quickActions.length > 0 && (
        <div className="gva-rail-empty-actions">
          <p className="gva-rail-empty-actions-label">
            {t('voice.emptyState.tryNow', 'Try now')}
          </p>
          <div className="gva-rail-empty-action-grid">
            {quickActions.map(({ label, command, dynamic }) => (
              <button
                key={command}
                type="button"
                className={`gva-rail-empty-action${dynamic ? ' gva-rail-empty-action--dynamic' : ''}`}
                onClick={() => onSuggestedQuery(command)}
                disabled={disabled}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tipDismissed && (
        <button type="button" className="gva-rail-empty-restore-tip" onClick={restoreTip}>
          {t('voice.showTip', 'Show getting started tip')}
        </button>
      )}
    </div>
  );
};

export default VoiceAssistantRailEmptyState;
